import React, { useState, useEffect } from 'react';
import { monitoringService } from '../services/authService';
import {
  ChartBarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ServerIcon,
  CpuChipIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

interface HealthStatus {
  status: string;
  timestamp: string;
  uptime: string;
  metrics: {
    requestCount: number;
    errorRate: string;
    avgResponseTime: string;
    avgDbQueryTime: string;
  };
  issues?: string[];
}

interface PerformanceMetrics {
  timeWindow: string;
  requests: {
    total: number;
    avgDuration: string;
    p50: string;
    p95: string;
    p99: string;
    byEndpoint: Array<{
      endpoint: string;
      count: number;
      avgDuration: string;
      p95: string;
    }>;
  };
  errors: {
    total: number;
    rate: string;
    recent: Array<{
      timestamp: string;
      path: string;
      statusCode: number;
      message: string;
    }>;
  };
  database: {
    totalQueries: number;
    avgDuration: string;
    slowQueries: Array<{
      model: string;
      operation: string;
      duration: number;
    }>;
  };
  cache: {
    totalOps: number;
    hits: number;
    misses: number;
    hitRate: string;
  };
  uptime: string;
}

const MonitoringPage: React.FC = () => {
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [timeWindow, setTimeWindow] = useState(60);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const loadData = async () => {
    try {
      const [healthData, metricsData] = await Promise.all([
        monitoringService.getHealthStatus(),
        monitoringService.getPerformanceMetrics(timeWindow),
      ]);
      setHealth(healthData);
      setMetrics(metricsData.data);
    } catch (error) {
      console.error('Failed to load monitoring data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [timeWindow]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(loadData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [autoRefresh, timeWindow]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-100 text-green-800';
      case 'degraded':
        return 'bg-yellow-100 text-yellow-800';
      case 'unhealthy':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon className="h-6 w-6 text-green-500" />;
      case 'degraded':
        return <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />;
      case 'unhealthy':
        return <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />;
      default:
        return <ServerIcon className="h-6 w-6 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sistem Monitörü</h1>
          <p className="mt-1 text-sm text-gray-500">
            API performansı ve sistem sağlığı
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Otomatik Yenile</span>
          </label>
          <button
            onClick={loadData}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Yenile
          </button>
        </div>
      </div>

      {/* Time Window Selector */}
      <div className="flex space-x-2">
        {[5, 15, 30, 60, 120].map((minutes) => (
          <button
            key={minutes}
            onClick={() => setTimeWindow(minutes)}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              timeWindow === minutes
                ? 'bg-primary-100 text-primary-700'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            } border border-gray-300`}
          >
            {minutes < 60 ? `${minutes}dk` : `${minutes / 60}sa`}
          </button>
        ))}
      </div>

      {/* Health Status Card */}
      {health && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {getStatusIcon(health.status)}
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Sistem Durumu</h2>
                <p className="text-sm text-gray-500">
                  Çalışma Süresi: {health.uptime}
                </p>
              </div>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                health.status
              )}`}
            >
              {health.status === 'healthy' ? 'Sağlıklı' :
               health.status === 'degraded' ? 'Düşük Performans' : 'Sorunlu'}
            </span>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-4">
            <div className="bg-gray-50 px-4 py-5 rounded-lg">
              <dt className="text-sm font-medium text-gray-500">İstek Sayısı</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">
                {health.metrics.requestCount}
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 rounded-lg">
              <dt className="text-sm font-medium text-gray-500">Hata Oranı</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">
                {health.metrics.errorRate}
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 rounded-lg">
              <dt className="text-sm font-medium text-gray-500">Ort. Yanıt</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">
                {health.metrics.avgResponseTime}
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 rounded-lg">
              <dt className="text-sm font-medium text-gray-500">Ort. DB Sorgusu</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">
                {health.metrics.avgDbQueryTime}
              </dd>
            </div>
          </div>

          {health.issues && health.issues.length > 0 && (
            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Tespit Edilen Sorunlar
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <ul className="list-disc pl-5 space-y-1">
                      {health.issues.map((issue, i) => (
                        <li key={i}>{issue}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Performance Metrics */}
      {metrics && (
        <>
          {/* Request Stats */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center space-x-2 mb-4">
              <ChartBarIcon className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-semibold text-gray-900">
                İstek Performansı ({metrics.timeWindow})
              </h2>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-5 mb-6">
              <div className="bg-blue-50 px-4 py-5 rounded-lg">
                <dt className="text-sm font-medium text-blue-600">Toplam İstek</dt>
                <dd className="mt-1 text-2xl font-semibold text-blue-900">
                  {metrics.requests.total}
                </dd>
              </div>
              <div className="bg-green-50 px-4 py-5 rounded-lg">
                <dt className="text-sm font-medium text-green-600">Ortalama</dt>
                <dd className="mt-1 text-2xl font-semibold text-green-900">
                  {metrics.requests.avgDuration}ms
                </dd>
              </div>
              <div className="bg-yellow-50 px-4 py-5 rounded-lg">
                <dt className="text-sm font-medium text-yellow-600 flex items-center gap-1">
                  P50
                  <span className="text-xs font-normal text-yellow-500" title="50. Percentile">ⓘ</span>
                </dt>
                <dd className="mt-1 text-2xl font-semibold text-yellow-900">
                  {metrics.requests.p50}ms
                </dd>
                <dd className="mt-1 text-xs text-yellow-600">
                  %50 daha hızlı
                </dd>
              </div>
              <div className="bg-orange-50 px-4 py-5 rounded-lg">
                <dt className="text-sm font-medium text-orange-600 flex items-center gap-1">
                  P95
                  <span className="text-xs font-normal text-orange-500" title="95. Percentile">ⓘ</span>
                </dt>
                <dd className="mt-1 text-2xl font-semibold text-orange-900">
                  {metrics.requests.p95}ms
                </dd>
                <dd className="mt-1 text-xs text-orange-600">
                  %95 daha hızlı
                </dd>
              </div>
              <div className="bg-red-50 px-4 py-5 rounded-lg">
                <dt className="text-sm font-medium text-red-600 flex items-center gap-1">
                  P99
                  <span className="text-xs font-normal text-red-500" title="99. Percentile">ⓘ</span>
                </dt>
                <dd className="mt-1 text-2xl font-semibold text-red-900">
                  {metrics.requests.p99}ms
                </dd>
                <dd className="mt-1 text-xs text-red-600">
                  %99 daha hızlı
                </dd>
              </div>
            </div>

            {/* Top Endpoints */}
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                En Çok Kullanılan Endpoint'ler
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Endpoint
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        İstek Sayısı
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Ortalama
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        P95
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {metrics.requests.byEndpoint.slice(0, 10).map((endpoint, i) => (
                      <tr key={i}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                          {endpoint.endpoint}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {endpoint.count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {endpoint.avgDuration}ms
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {endpoint.p95}ms
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Database Performance */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center space-x-2 mb-4">
              <CpuChipIcon className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-semibold text-gray-900">
                Veritabanı Performansı
              </h2>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 mb-6">
              <div className="bg-purple-50 px-4 py-5 rounded-lg">
                <dt className="text-sm font-medium text-purple-600">Toplam Sorgu</dt>
                <dd className="mt-1 text-2xl font-semibold text-purple-900">
                  {metrics.database.totalQueries}
                </dd>
              </div>
              <div className="bg-indigo-50 px-4 py-5 rounded-lg">
                <dt className="text-sm font-medium text-indigo-600">Ortalama Süre</dt>
                <dd className="mt-1 text-2xl font-semibold text-indigo-900">
                  {metrics.database.avgDuration}ms
                </dd>
              </div>
            </div>

            {metrics.database.slowQueries.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Yavaş Sorgular (&gt;500ms)
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Model
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          İşlem
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Süre
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {metrics.database.slowQueries.map((query, i) => (
                        <tr key={i}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {query.model}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {query.operation}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600">
                            {query.duration}ms
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Cache Performance */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center space-x-2 mb-4">
              <ClockIcon className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-semibold text-gray-900">Cache Performansı</h2>
            </div>

            {metrics.cache.totalOps === 0 ? (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-6 text-center">
                <ClockIcon className="h-12 w-12 text-blue-300 mx-auto mb-3" />
                <h3 className="text-sm font-medium text-blue-900 mb-2">
                  Cache Henüz Kullanılmadı
                </h3>
                <p className="text-sm text-blue-700">
                  Seçilen zaman aralığında cache işlemi gerçekleştirilmedi.
                  <br />
                  Dashboard'a birkaç kez girerek cache'i aktifleştirin.
                </p>
                <p className="text-xs text-blue-600 mt-3">
                  💡 Cache, sık kullanılan verileri hızlandırır (Dashboard stats 60sn cache'leniyor)
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
                <div className="bg-teal-50 px-4 py-5 rounded-lg">
                  <dt className="text-sm font-medium text-teal-600">Toplam İşlem</dt>
                  <dd className="mt-1 text-2xl font-semibold text-teal-900">
                    {metrics.cache.totalOps}
                  </dd>
                </div>
                <div className="bg-green-50 px-4 py-5 rounded-lg">
                  <dt className="text-sm font-medium text-green-600 flex items-center gap-1">
                    Hit
                    <span className="text-xs font-normal text-green-500" title="Veri cache'de bulundu">ⓘ</span>
                  </dt>
                  <dd className="mt-1 text-2xl font-semibold text-green-900">
                    {metrics.cache.hits}
                  </dd>
                  <dd className="mt-1 text-xs text-green-600">
                    Cache'den hızlı
                  </dd>
                </div>
                <div className="bg-red-50 px-4 py-5 rounded-lg">
                  <dt className="text-sm font-medium text-red-600 flex items-center gap-1">
                    Miss
                    <span className="text-xs font-normal text-red-500" title="Veri cache'de bulunamadı">ⓘ</span>
                  </dt>
                  <dd className="mt-1 text-2xl font-semibold text-red-900">
                    {metrics.cache.misses}
                  </dd>
                  <dd className="mt-1 text-xs text-red-600">
                    DB'den yavaş
                  </dd>
                </div>
                <div className="bg-blue-50 px-4 py-5 rounded-lg">
                  <dt className="text-sm font-medium text-blue-600 flex items-center gap-1">
                    Hit Rate
                    <span className="text-xs font-normal text-blue-500" title="Başarı oranı">ⓘ</span>
                  </dt>
                  <dd className="mt-1 text-2xl font-semibold text-blue-900">
                    {metrics.cache.hitRate}
                  </dd>
                  <dd className="mt-1 text-xs text-blue-600">
                    {parseFloat(metrics.cache.hitRate) > 50 ? '✅ İyi' : '⚠️ Düşük'}
                  </dd>
                </div>
              </div>
            )}
          </div>

          {/* Errors */}
          {metrics.errors.total > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center space-x-2 mb-4">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Hatalar ({metrics.errors.total} - {metrics.errors.rate})
                </h2>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Zaman
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Path
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Mesaj
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {metrics.errors.recent.map((error, i) => (
                      <tr key={i}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(error.timestamp).toLocaleString('tr-TR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                          {error.path}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                            {error.statusCode}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {error.message}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MonitoringPage;
